import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { FloorPlanSelection } from '@hospora-bookings/shared-ui'

export interface BookingState {
  venueId: string | null
  selection: FloorPlanSelection | null
  date: string | null
  timeSlot: string | null
  pricing: {
    minimumSpend: number
    depositAmount: number
    requiresDeposit: boolean
    priceSource: string
    breakdown?: string
  } | null
  customerFirstName: string
  customerLastName: string
  customerEmail: string
  customerPhone: string
  partySize: number
  notes: string
  createdBookingId: string | null
}

import { apiBaseUrl } from '../lib/runtime'
const API_URL = apiBaseUrl()

export type BookingRequirement = 'highchair' | 'pram' | 'wheelchair'
export interface BookedForDetails { firstName: string; lastName: string; phone: string; email: string }

export const useBookingStore = defineStore('booking', () => {
  const venueId = ref<string | null>(null)
  const selection = ref<FloorPlanSelection | null>(null)
  const date = ref<string | null>(null)
  const timeSlot = ref<string | null>(null)
  const pricing = ref<BookingState['pricing']>(null)
  const customerFirstName = ref('')
  const customerLastName = ref('')
  const customerEmail = ref('')
  const customerPhone = ref('')
  const partySize = ref(2)
  const durationHours = ref(2)       // how long the customer plans to stay
  const startTime = ref<string | null>(null) // specific HH:MM arrival time (table bookings)
  const areaType = ref<'STANDARD' | 'PRIVATE'>('STANDARD')
  const sectionPreference = ref<string | null>(null) // preferred section ID for table bookings
  const hasSpecialRequests = ref(false)              // explicit special-requests checkbox
  const notes = ref('')
  const createdBookingId = ref<string | null>(null)
  const manageToken = ref<string | null>(null)       // customer manage JWT returned by API
  // Classification result returned by the API after booking creation
  const classification = ref<any>(null)

  // 'large_group' once the party is bigger than one table booking may be (set from the party size alone);
  // 'function_event' only when the guest chose venue hire themselves. Both finish on the group booking step.
  const largeBookingType = ref<'large_group' | 'function_event' | null>(null)
  const occasion = ref('')
  // Ticked needs (highchair, pram, wheelchair) and the person dining when the guest books for someone else.
  const requirements = ref<BookingRequirement[]>([])
  const bookingForSomeoneElse = ref(false)
  const bookedFor = ref<BookedForDetails>({ firstName: '', lastName: '', phone: '', email: '' })
  // True once the user has confirmed their party size on the dedicated Guests step
  const guestsConfirmed = ref(false)

  const venues = ref<any[]>([])
  const venueDetails = ref<any>(null)
  const availability = ref<any>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  let availabilityRequestId = 0

  const currentVenue = computed(() => venues.value.find((v) => v.id === venueId.value))

  async function fetchVenues() {
    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_URL}/venues`)
      if (!res.ok) throw new Error('Failed to fetch venues')
      venues.value = await res.json()
    } catch (e: any) {
      error.value = e.message
    } finally {
      loading.value = false
    }
  }

  async function fetchVenueDetails(id: string) {
    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_URL}/venues/${id}`)
      if (!res.ok) throw new Error('Failed to fetch venue')
      venueDetails.value = await res.json()
    } catch (e: any) {
      error.value = e.message
    } finally {
      loading.value = false
    }
  }

  async function fetchAvailability(vid: string, d: string) {
    const requestId = ++availabilityRequestId
    loading.value = true
    error.value = null
    try {
      const ps = partySize.value > 0 ? `&partySize=${partySize.value}` : ''
      const bookingType = largeBookingType.value || selection.value?.mode || 'table'
      const typeParam = `&bookingType=${encodeURIComponent(bookingType)}`
      const res = await fetch(`${API_URL}/timeslots/available?venueId=${vid}&date=${d}${ps}${typeParam}`)
      if (!res.ok) throw new Error('Failed to fetch availability')
      const data = await res.json()
      if (requestId === availabilityRequestId) availability.value = data
      return data
    } catch (e: any) {
      if (requestId === availabilityRequestId) {
        availability.value = null
        error.value = e.message
      }
      return null
    } finally {
      if (requestId === availabilityRequestId) loading.value = false
    }
  }

  async function fetchPricing(vid: string, d: string, slot: string, bookingType: string) {
    try {
      const res = await fetch(
        `${API_URL}/timeslots/pricing?venueId=${vid}&date=${d}&timeSlot=${encodeURIComponent(slot)}&bookingType=${bookingType}`
      )
      if (!res.ok) throw new Error('Failed to fetch pricing')
      pricing.value = await res.json()
    } catch (e: any) {
      error.value = e.message
    }
  }

  /** The person dining, when booking for someone else and both names are given. */
  function bookedForPayload() {
    const b = bookedFor.value
    if (!bookingForSomeoneElse.value || !b.firstName.trim() || !b.lastName.trim()) return null
    return {
      firstName: b.firstName.trim(), lastName: b.lastName.trim(),
      ...(b.phone.trim() ? { phone: b.phone.trim() } : {}),
      ...(b.email.trim() ? { email: b.email.trim() } : {}),
    }
  }

  /** Occasion, needs and the person dining, as the table and pool-table create routes take them. */
  function guestExtrasPayload() {
    const diner = bookedForPayload()
    return {
      ...(occasion.value ? { occasion: occasion.value } : {}),
      ...(requirements.value.length ? { requirements: [...requirements.value] } : {}),
      ...(diner ? { bookedFor: diner } : {}),
    }
  }

  /**
   * Notes for the group and venue-hire routes, which have no fields for needs or the person dining: those are
   * written into the notes the venue reads.
   */
  const notesWithExtras = computed(() => {
    const labels: Record<BookingRequirement, string> = { highchair: 'Highchair', pram: 'Pram', wheelchair: 'Wheelchair / walker access' }
    const diner = bookedForPayload()
    return [
      notes.value.trim(),
      requirements.value.length ? `Needs: ${requirements.value.map((r) => labels[r]).join(', ')}` : '',
      diner ? `Booking for: ${[`${diner.firstName} ${diner.lastName}`, diner.phone, diner.email].filter(Boolean).join(' · ')}` : '',
    ].filter(Boolean).join('\n')
  })

  async function createBooking() {
    loading.value = true
    error.value = null
    try {
      const body: any = {
        venueId: venueId.value,
        bookingType: selection.value?.mode,
        date: date.value,
        timeSlot: timeSlot.value,
        customerFirstName: customerFirstName.value,
        customerLastName: customerLastName.value,
        customerEmail: customerEmail.value,
        customerPhone: customerPhone.value,
        partySize: partySize.value,
        durationHours: durationHours.value,
        areaType: areaType.value,
        hasSpecialRequests: hasSpecialRequests.value,
        notes: notes.value,
        ...guestExtrasPayload(),
      }

      if (selection.value?.mode === 'section') {
        body.sectionId = selection.value.selectedSectionId
      }
      if (selection.value?.mode === 'table') {
        body.tableIds = selection.value.selectedTableIds
        body.bookingType = 'table'
        if (sectionPreference.value) body.sectionPreference = sectionPreference.value
        if (startTime.value) body.startTime = startTime.value
      }
      if (selection.value?.mode === 'pool_table') {
        body.bookingType = 'pool_table'
        body.poolTableId = (selection.value as any).selectedPoolTableId
        body.poolTableHours = (selection.value as any).poolTableHours || 1
        body.poolTableStartTime = startTime.value
      }

      const res = await fetch(`${API_URL}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.error || 'Failed to create booking')
      }

      const data = await res.json()
      createdBookingId.value = data.booking.id
      pricing.value = data.pricing
      classification.value = data.classification || null
      manageToken.value = data.manageToken || null
      return data
    } catch (e: any) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Payment-first pool table booking.
   * Charges the customer's card via Square, then creates the confirmed booking.
   * No booking is created if the card charge fails.
   *
   * @param paymentToken - One-time token from Square Web Payments SDK tokenize()
   */
  async function chargeAndCreatePoolTableBooking(paymentToken: string, verificationToken?: string, expectedTotalCents?: number) {
    loading.value = true
    error.value = null
    try {
      const body: any = {
        paymentToken,
        ...(verificationToken ? { verificationToken } : {}),
        ...(typeof expectedTotalCents === 'number' ? { expectedTotalCents } : {}),
        venueId: venueId.value,
        bookingType: 'pool_table',
        poolTableId: (selection.value as any).selectedPoolTableId,
        poolTableHours: (selection.value as any).poolTableHours || 1,
        poolTableStartTime: startTime.value,
        date: date.value,
        timeSlot: timeSlot.value,
        customerFirstName: customerFirstName.value,
        customerLastName: customerLastName.value,
        customerEmail: customerEmail.value,
        customerPhone: customerPhone.value,
        partySize: partySize.value,
        notes: notes.value,
        ...guestExtrasPayload(),
      }

      const res = await fetch(`${API_URL}/payments/pool-table-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        const err = await res.json()
        const failure = new Error(err.error || 'Payment failed. Please try again.') as Error & { code?: string }
        if (typeof err.code === 'string') failure.code = err.code
        throw failure
      }

      const data = await res.json()
      createdBookingId.value = data.booking.id
      pricing.value = data.pricing
      manageToken.value = data.manageToken || null
      return data
    } catch (e: any) {
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  async function getBooking(id: string) {
    const res = await fetch(`${API_URL}/bookings/${id}`)
    if (!res.ok) throw new Error('Booking not found')
    return res.json()
  }

  async function getBookingWithToken(id: string, token: string) {
    const res = await fetch(`${API_URL}/bookings/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new Error('Booking not found or token expired')
    return res.json()
  }

  async function cancelBookingAsCustomer(id: string, token: string) {
    const res = await fetch(`${API_URL}/bookings/${id}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    if (!res.ok) {
      const err = await res.json()
      throw new Error(err.error || 'Failed to cancel booking')
    }
    return res.json()
  }

  async function rescheduleBooking(id: string, token: string, date: string, timeSlot: string, startTime?: string) {
    const res = await fetch(`${API_URL}/bookings/${id}/reschedule`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ date, timeSlot, ...(startTime ? { startTime } : {}) }),
    })
    if (!res.ok) {
      const err = await res.json()
      throw new Error(err.error || 'Failed to reschedule booking')
    }
    return res.json()
  }

  async function updatePartySize(id: string, token: string, partySize: number) {
    const res = await fetch(`${API_URL}/bookings/${id}/party-size`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ partySize }),
    })
    if (!res.ok) {
      const err = await res.json()
      throw new Error(err.error || 'Failed to update party size')
    }
    return res.json()
  }

  async function lookupBooking(email: string, reference: string) {
    const res = await fetch(`${API_URL}/bookings/lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, reference }),
    })
    if (!res.ok) {
      const err = await res.json()
      throw new Error(err.error || 'Booking not found')
    }
    return res.json()
  }

  function reset() {
    venueId.value = null
    selection.value = null
    date.value = null
    timeSlot.value = null
    pricing.value = null
    customerFirstName.value = ''
    customerLastName.value = ''
    customerEmail.value = ''
    customerPhone.value = ''
    partySize.value = 2
    durationHours.value = 2
    startTime.value = null
    areaType.value = 'STANDARD'
    sectionPreference.value = null
    hasSpecialRequests.value = false
    notes.value = ''
    occasion.value = ''
    requirements.value = []
    bookingForSomeoneElse.value = false
    bookedFor.value = { firstName: '', lastName: '', phone: '', email: '' }
    createdBookingId.value = null
    classification.value = null
    manageToken.value = null
    largeBookingType.value = null
    guestsConfirmed.value = false
  }

  return {
    venueId, selection, date, timeSlot, startTime, pricing, classification, manageToken, largeBookingType, guestsConfirmed,
    customerFirstName, customerLastName, customerEmail, customerPhone,
    partySize, durationHours, areaType, sectionPreference, hasSpecialRequests, notes, occasion,
    requirements, bookingForSomeoneElse, bookedFor, notesWithExtras,
    createdBookingId, venues, venueDetails, availability,
    loading, error, currentVenue,
    fetchVenues, fetchVenueDetails, fetchAvailability,
     fetchPricing, createBooking, chargeAndCreatePoolTableBooking, getBooking,
    getBookingWithToken, cancelBookingAsCustomer, rescheduleBooking, updatePartySize, lookupBooking,
    reset,
  }
})
